<script setup lang="ts">
import type { ColorVariant } from '~/utils/theme'

const iconModules = import.meta.glob('~/assets/img/icons/*.svg', { eager: true, query: '?raw', import: 'default' }) as Record<string, string>

const props = defineProps<{
	color: ColorVariant
	tag?: string
	href?: string
	type?: 'button' | 'submit' | 'reset'
	icon?: string
	iconPosition?: 'left' | 'right'
	disabled?: boolean
}>()

const isSvgIcon = computed(() => props.icon?.endsWith('.svg'))
const svgContent = computed(() => {
	if (!isSvgIcon.value || !props.icon) return undefined
	const key = Object.keys(iconModules).find((k) => k.endsWith(`/${props.icon}`))
	const raw = key ? iconModules[key] : undefined
	return raw?.replace(/stroke="[^"]*"/g, 'stroke="currentColor"').replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
})
</script>

<template>
	<component
		:is="href ? 'a' : tag || 'button'"
		:href="href || undefined"
		:type="!href && !tag ? (type || 'button') : undefined"
		:disabled="!href && !tag ? disabled : undefined"
		:aria-disabled="disabled || undefined"
		:data-color="color"
		:data-disabled="disabled || undefined"
		class="group flex w-[100%] items-center justify-center gap-2 border-2 border-[--color-border] bg-[--color-bg] px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-[--color-border] focus:ring-offset-2"
		:class="[
			disabled ? '' : 'cursor-pointer hover:bg-[--color-hover-bg]',
			{ 'flex-row-reverse': iconPosition === 'right' },
		]"
	>
		<span
			v-if="isSvgIcon && svgContent"
			class="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[--color-text] transition-colors [&>svg]:h-full [&>svg]:w-full"
			:class="{ 'group-hover:text-[--color-hover-text]': !disabled }"
			v-html="svgContent"
		/>
		<v-icon
			v-else-if="icon"
			:name="icon"
			class="h-5 w-5 text-[--color-text] transition-colors"
			:class="{ 'group-hover:text-[--color-hover-text]': !disabled }"
		/>
		<span
			class="text-sm font-bold text-[--color-text] md:text-base"
			:class="{ 'group-hover:text-[--color-hover-text]': !disabled }"
		>
			<slot />
		</span>
	</component>
</template>
